import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { MatDrawer } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { WellHeaderInfoService } from 'app/modules/well-header-information/services/well-header-info.service';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { AssetsNode, ExampleFlatNode } from 'app/modules/well-header-information/model/tree-node.model';
import { AddCompanyModalComponent } from 'app/modules/well-header-information/pages/assets/add-company-modal/add-company-modal.component';
import { ConfirmationModalDialogComponent } from 'app/core/shared/component/confirmation-modal-dialog/confirmation-modal-dialog.component';
import module from "app/modules/well-header-information/well-header-information.module";

@Component({
  selector: 'app-preventive-maintainance',
  templateUrl: './assets.page.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: module,
  styleUrls: ['./assets.page.scss']
})
export class AssetsPage {
    @ViewChild('drawer') drawer!: MatDrawer;

    public selectedField: string
    public selectedPlatform: string
    public showFiller = true;
    public assetsNode: AssetsNode = {
      name: '',
      longName: '',
      level: ''
    }
    public selectedNode: any = this.assetsNode
  
    // Default appear drawer navigation
    ngAfterViewInit(): void {
      setTimeout(() => {
        this.drawer.toggle();
      }, 0);
    }
  
    //  TREE NODE CONTROLLER
    private transformer = (node: AssetsNode, level: number) => {
      return {
        expandable: !!node.children && node.children.length > 0,
        name: node.name,
        longName: node.longName,
        id: node[node.level.toLowerCase() + 'Id'] || node.operatorId || node.pscId,
        nodeLevel: node.level,
        object: node,
        level: level,
      };
    }
  
    treeControl = new FlatTreeControl<ExampleFlatNode>(
      node => node.level, node => node.expandable);
  
    treeFlattener = new MatTreeFlattener(
      this.transformer, node => node.level, node => node.expandable, node => node.children);
  
    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  
    constructor(
      public dialog: MatDialog,
      private wellHeaderService: WellHeaderInfoService,
      private router: Router,
    ) { }
  
    hasChild = (_: number, node: ExampleFlatNode) => node.expandable;
  
    ngOnInit(): void {
      this.getAssetTree()
  
      // Subscribe to the observable
      this.wellHeaderService.assetsTree$.subscribe(data => {
        if (!data) return;
        this.dataSource.data = data;
      });
  
      this.wellHeaderService.parentsNode$.subscribe(parentIds => {
        
        setTimeout(() => {
          if (parentIds?.length != 0) {
            parentIds?.forEach((parentId) => {
              let parentsNode = this.treeControl.dataNodes.find(obj => obj.id == parentId)
              this.treeControl.expand(parentsNode);
            })
          }
        }, 300);
  
      })
    }
  
    pressTree(node) {
      this.selectedNode = node;
  
      const routeMap = {
        'Company': 'company',
        'Region': 'region',
        'Field': 'field',
        'Platform': 'platform'
      };
  
      const routeSegment = routeMap[node.nodeLevel];
  
      if (routeSegment) {
        this.router.navigate([`data-management/well-header-info/assets/${routeSegment}`, node.id]);
        this.treeControl.expand(node);
      }
    }
  
    getAssetTree() {
      this.wellHeaderService.getAssetTree().subscribe()
    }
  
    addCompanyModal() {
      const dialogRef = this.dialog.open(AddCompanyModalComponent, {
        width: "50%"
      });
  
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.getAssetTree()
        }
      });
    }
  
    getIcon(nodeLevel) {
      return nodeLevel == 'Company'
        ? 'heroicons_outline:building-office-2'
        : 'heroicons_outline:globe-asia-australia'
    }
  
    deleteNode(node) {
      const dialogRef = this.dialog.open(ConfirmationModalDialogComponent, {
        data: {
          title: 'Deleting Node',
          description: 'Would you like to proceed with deleting this item ?',
          selectedItemName: node.longName
        },
        width: "40%"
      });
  
      dialogRef.afterClosed().subscribe(isConfirm => {
        if (isConfirm) {
          let foundParentNode = this.findParentsNode(node)
  
          const deleteMethods = {
            'Platform': (id) => this.wellHeaderService.deletePlatform(id),
            'Field': (id) => this.wellHeaderService.deleteFields(id),
            'Region': (id) => this.wellHeaderService.deletePsc(id),
            'Company': (id) => this.wellHeaderService.deleteCompany(id)
          };
  
          const deleteObservable = deleteMethods[node.nodeLevel]?.(node.id);
  
          if (deleteObservable) {
            deleteObservable.subscribe(() => {
              this.getAssetTree();
  
              setTimeout(() => {
                foundParentNode.forEach(parentsNode => {
                  let foundDeletedParentsNode = this.treeControl.dataNodes.find(obj => obj.id == parentsNode.id)
                  this.treeControl.expand(foundDeletedParentsNode);
                })
              }, 400);
            });
          }
        }
      });
    }
  
    findParentsNode(node) {
      if (node.level == 0) return
  
      let parentsNode = []
      let nodeLevel = node.level
  
      while (nodeLevel > 0) {
        const foundDeletedObject = this.treeControl.dataNodes.find(obj => obj.id === node.id);
        const deletedIndex = this.treeControl.dataNodes.indexOf(foundDeletedObject);
        const remainingDataNode = this.treeControl.dataNodes.slice(0, deletedIndex + 1);
        const foundParentsNode = remainingDataNode.reverse().find(obj => obj.level === nodeLevel - 1);
  
        parentsNode.push(foundParentsNode)
        nodeLevel--
      }
  
      return parentsNode
    }
}
